import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import { useI18n, usePageTitle } from '../../i18n/index.jsx';
import { Alert, Field } from '../../components/ui.jsx';
import { AccountHead } from './AccountLayout.jsx';
import SignatureEditor from '../../components/SignatureEditor.jsx';

const PROVIDERS = ['zoho', 'zoho_pro', 'gmail', 'outlook', 'hostinger', 'other'];
const SECURITY = ['ssl', 'starttls', 'none'];
const EMPTY = { provider: 'zoho', displayName: '', email: '', username: '', password: '', smtpHost: '', smtpPort: '', smtpSecurity: 'ssl', imapHost: '', imapPort: '', imapSecurity: 'ssl', saveSent: true, importReplies: true };

// Preset (server names) → form fields.
const fromPreset = (p) => (p && p.smtp_host ? { smtpHost: p.smtp_host, smtpPort: p.smtp_port, smtpSecurity: p.smtp_security, imapHost: p.imap_host, imapPort: p.imap_port, imapSecurity: p.imap_security } : {});

// "Ma boîte courriel / My mailbox" (migration 032): the user's own mailbox,
// so emails and documents go out from their address, with a copy in their
// "Sent" folder, and customers' replies come back to the timeline. The
// password is sent once and never shown again.
//
// Three separate cards, each with its own buttons and messages: My mailbox,
// Company rules, Connected mailboxes. The buttons are never silently
// disabled (only while working): a missing field is shown in red instead
// (the browser's own check can't point at fields inside the closed
// "Servers" panel, so the form uses noValidate).
const validPort = (v) => { const n = Number(v); return Number.isInteger(n) && n > 0 && n < 65536; };

export default function MailboxPage() {
  const { t, errorText } = useI18n();
  const { user } = useAuth();
  usePageTitle('accountNav.mailbox');
  // Also the admins' mailbox (/admin/mailbox): its own tutorials place.
  const mailboxPlace = useLocation().pathname.startsWith('/admin') ? 'adm.mailbox' : 'acc.mailbox';
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [f, setF] = useState(EMPTY);
  const [errs, setErrs] = useState({});
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState({ kind: '', text: '' });          // My mailbox only
  const [rulesMsg, setRulesMsg] = useState({ kind: '', text: '' }); // Company rules only
  const [test, setTest] = useState(null);
  const [rules, setRules] = useState(null);       // saved
  const [rulesDraft, setRulesDraft] = useState(null);
  const [team, setTeam] = useState(null);
  const [serversOpen, setServersOpen] = useState(false);

  function apply(d) {
    setData(d);
    const m = d.mailbox;
    setF(m ? { ...EMPTY, provider: m.provider, displayName: m.displayName || '', email: m.email, username: m.username, smtpHost: m.smtpHost, smtpPort: m.smtpPort, smtpSecurity: m.smtpSecurity,
      imapHost: m.imapHost || '', imapPort: m.imapPort || '', imapSecurity: m.imapSecurity, saveSent: m.saveSent, importReplies: m.importReplies }
      : { ...EMPTY, displayName: d.defaults.displayName || '', email: d.defaults.email || '', username: d.defaults.email || '', ...fromPreset(d.presets.zoho) });
    setRules(d.rules); setRulesDraft(d.rules);
    setServersOpen(!m ? false : m.provider === 'other');
  }
  useEffect(() => { api('/mailbox').then(apply).catch((e) => setLoadErr(errorText(e))); }, [errorText]);
  const loadTeam = () => api('/mailbox/team').then(setTeam).catch(() => {});
  useEffect(() => { if (data?.manageRules) loadTeam(); }, [data?.manageRules]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => { setF((x) => ({ ...x, [k]: v })); setErrs((e) => (e[k] ? { ...e, [k]: null } : e)); };
  const pickProvider = (p) => { setF((x) => ({ ...x, provider: p, ...fromPreset(data.presets[p]) })); if (p === 'other') setServersOpen(true); };
  const say = (kind, text) => setMsg({ kind, text });

  const m = data?.mailbox;
  const passwordKept = !!(m?.hasPassword && m.passwordReadable);
  // Missing / wrong fields → { field: message } (shown in red, never a dead button).
  function check() {
    const e = {};
    if (!f.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = t(f.email.trim() ? 'mailbox.invalid.email' : 'mailbox.missing');
    if (!f.username.trim()) e.username = t('mailbox.missing');
    if (!f.password && !passwordKept) e.password = t('mailbox.missing');
    if (!f.smtpHost.trim()) e.smtpHost = t('mailbox.missing');
    if (!validPort(f.smtpPort)) e.smtpPort = t(f.smtpPort === '' ? 'mailbox.missing' : 'mailbox.invalid.port');
    if (f.imapHost.trim() && !validPort(f.imapPort)) e.imapPort = t(f.imapPort === '' ? 'mailbox.missing' : 'mailbox.invalid.port');
    return e;
  }

  async function save(andTest) {
    setTest(null);
    const e = check();
    setErrs(e);
    const bad = Object.keys(e);
    if (bad.length) {
      if (bad.some((k) => ['smtpHost', 'smtpPort', 'imapPort'].includes(k))) setServersOpen(true);
      say('error', t('mailbox.missingList', { fields: bad.map((k) => t(`mailbox.fieldName.${k}`)).join(', ') }));
      return;
    }
    if (f.password && !data.encryptionReady) { say('error', t('errors.mailbox_key_missing')); return; }
    setBusy(andTest ? 'test' : 'save'); say('', '');
    let saved = false;
    try {
      const r = await api('/mailbox', { method: 'PUT', body: { ...f, password: f.password || undefined } });
      saved = true;
      setData((d) => ({ ...d, mailbox: r.mailbox })); set('password', '');
      if (andTest) {
        // Saved first: a failed test never loses the settings.
        setBusy('test');
        const x = await api('/mailbox/test', { method: 'POST' });
        setTest(x); setData((d) => ({ ...d, mailbox: x.mailbox }));
        say(x.mailbox.connected ? 'success' : 'error', x.mailbox.connected ? t('mailbox.connectedOk') : t('mailbox.savedTestFailed'));
      } else say('success', t('mailbox.saved'));
    } catch (err) {
      say('error', saved ? `${t('mailbox.savedTestFailed')} ${errorText(err)}` : errorText(err));
    } finally {
      setBusy('');
      if (data.manageRules) loadTeam();
    }
  }
  async function act(name, fn) {
    setBusy(name); say('', '');
    try { await fn(); } catch (e) { say('error', errorText(e)); } finally { setBusy(''); }
  }
  const disconnect = () => act('remove', async () => {
    if (!window.confirm(t('mailbox.removeConfirm'))) return;
    await api('/mailbox', { method: 'DELETE' }); setTest(null); setErrs({});
    apply(await api('/mailbox')); say('success', t('mailbox.removed'));
    if (data.manageRules) loadTeam();
  });
  const sync = () => act('sync', async () => {
    const r = await api('/mailbox/sync', { method: 'POST' });
    setData((d) => ({ ...d, mailbox: r.mailbox }));
    say(r.error ? 'error' : 'success', r.error ? hint(r.error, 'imap') : t('mailbox.synced', { n: r.imported }));
  });
  async function saveRules(e) {
    e.preventDefault();
    setBusy('rules'); setRulesMsg({ kind: '', text: '' });
    try {
      const r = await api('/mailbox/rules', { method: 'PUT', body: rulesDraft });
      setRules(r.rules); setRulesDraft(r.rules); setRulesMsg({ kind: 'success', text: t('mailbox.rulesSaved') });
    } catch (err) { setRulesMsg({ kind: 'error', text: errorText(err) }); } finally { setBusy(''); }
  }

  // Plain-language error, with the provider's own steps when known.
  function hint(code, side) {
    const provider = f.provider.startsWith('zoho') ? 'zoho' : f.provider;
    const specific = `mailbox.err.${code}_${provider}`;
    const text = t(specific);
    return `${side === 'imap' ? 'IMAP' : 'SMTP'} — ${text !== specific ? text : t(`mailbox.err.${code}`)}`;
  }

  if (!data) return <div className="acc-page"><AccountHead place={mailboxPlace} title={t('accountNav.mailbox')} />{loadErr ? <Alert>{loadErr}</Alert> : <p className="muted">{t('common.loading')}</p>}</div>;
  const status = !m ? 'none' : m.connected ? 'ok' : m.status === 'error' ? 'error' : !m.passwordReadable ? 'password' : 'untested';
  const lastErr = m?.lastError ? m.lastError.split(':') : null;
  const err = (k) => errs[k] && <p className="field-error" role="alert">{errs[k]}</p>;
  const inv = (k) => (errs[k] ? 'is-invalid' : undefined);

  return (
    <div className="acc-page">
      <AccountHead place={mailboxPlace} title={t('accountNav.mailbox')} lead={t('mailbox.lead')} />
      {/* Not the platform email (system emails): only super admins manage that one. */}
      <p className="callout callout-info small">{t('mailbox.notPlatform')} {user.isSuperAdmin ? <Link to="/admin/email">{t('mailbox.toPlatform')} →</Link> : t('mailbox.platformAuto')}</p>
      {!data.encryptionReady && <p className="callout callout-warning" role="alert"><strong>{t('mailbox.keyMissing')}</strong></p>}

      <form className="card form mb-form" noValidate onSubmit={(e) => { e.preventDefault(); save(true); }}>
        <h2 className="acc-card-title">{t('mailbox.account')}</h2>
        <div className={`callout ${status === 'ok' ? 'callout-success' : status === 'error' || status === 'password' ? 'callout-warning' : 'callout-info'}`} role="status">
          <p><strong>{t(`mailbox.status.${status}`, { email: m?.email || '' })}</strong></p>
          {status === 'none' && <p className="small">{t('mailbox.fallback', { email: user.email })}</p>}
          {status === 'error' && lastErr && <p className="small">{hint(lastErr[1], lastErr[0])}</p>}
          {m?.lastSyncAt && m.importReplies && status === 'ok' && <p className="small">{t('mailbox.lastSync', { date: new Date(m.lastSyncAt).toLocaleString(user.lang === 'en' ? 'en-CA' : 'fr-CA') })}</p>}
        </div>
        {rules?.requireOwn && status !== 'ok' && <p className="callout callout-warning small">{t('mailbox.banner.required')}</p>}
        <Field label={t('mailbox.provider')} htmlFor="mb-provider">
          <select id="mb-provider" value={f.provider} onChange={(e) => pickProvider(e.target.value)}>
            {PROVIDERS.map((p) => <option key={p} value={p}>{t(`mailbox.providers.${p}`)}</option>)}
          </select>
        </Field>
        {t(`mailbox.providerHint.${f.provider}`) !== `mailbox.providerHint.${f.provider}` && <p className="field-hint">{t(`mailbox.providerHint.${f.provider}`)}</p>}
        <div className="grid-form">
          <Field label={t('mailbox.displayName')} htmlFor="mb-name"><input id="mb-name" value={f.displayName} onChange={(e) => set('displayName', e.target.value)} maxLength={120} placeholder={user.name} /></Field>
          <Field label={t('mailbox.email')} htmlFor="mb-email">
            <input id="mb-email" type="email" className={inv('email')} value={f.email} onChange={(e) => { const v = e.target.value; setF((x) => ({ ...x, email: v, username: !x.username || x.username === x.email ? v : x.username })); setErrs((x) => ({ ...x, email: null, username: null })); }} maxLength={190} autoComplete="email" />
            {err('email')}
          </Field>
          <Field label={t('mailbox.username')} htmlFor="mb-user"><input id="mb-user" className={inv('username')} value={f.username} onChange={(e) => set('username', e.target.value)} maxLength={190} autoComplete="off" />{err('username')}</Field>
          <Field label={t('mailbox.password')} htmlFor="mb-pass">
            <input id="mb-pass" type="password" className={inv('password')} value={f.password} onChange={(e) => set('password', e.target.value)} maxLength={500} autoComplete="new-password"
              placeholder={passwordKept ? t('mailbox.passwordKept') : ''} />
            {err('password')}
          </Field>
        </div>
        <p className="field-hint">{t('mailbox.passwordHint')}</p>

        <details open={serversOpen} onToggle={(e) => setServersOpen(e.currentTarget.open)}>
          <summary><strong>{t('mailbox.servers')}</strong> <span className="muted small">{f.smtpHost}:{f.smtpPort} · {f.imapHost}{f.imapHost ? `:${f.imapPort}` : ''}</span></summary>
          <div className="grid-form">
            <Field label={t('mailbox.smtpHost')} htmlFor="mb-sh"><input id="mb-sh" className={inv('smtpHost')} value={f.smtpHost} onChange={(e) => set('smtpHost', e.target.value)} maxLength={190} />{err('smtpHost')}</Field>
            <Field label={t('mailbox.port')} htmlFor="mb-sp"><input id="mb-sp" type="number" className={inv('smtpPort')} min="1" max="65535" value={f.smtpPort} onChange={(e) => set('smtpPort', e.target.value)} />{err('smtpPort')}</Field>
            <Field label={t('mailbox.security')} htmlFor="mb-ss"><select id="mb-ss" value={f.smtpSecurity} onChange={(e) => set('smtpSecurity', e.target.value)}>{SECURITY.map((x) => <option key={x} value={x}>{t(`mailbox.sec.${x}`)}</option>)}</select></Field>
            <Field label={t('mailbox.imapHost')} htmlFor="mb-ih"><input id="mb-ih" value={f.imapHost} onChange={(e) => set('imapHost', e.target.value)} maxLength={190} /></Field>
            <Field label={t('mailbox.port')} htmlFor="mb-ip"><input id="mb-ip" type="number" className={inv('imapPort')} min="1" max="65535" value={f.imapPort} onChange={(e) => set('imapPort', e.target.value)} />{err('imapPort')}</Field>
            <Field label={t('mailbox.security')} htmlFor="mb-is"><select id="mb-is" value={f.imapSecurity} onChange={(e) => set('imapSecurity', e.target.value)}>{SECURITY.map((x) => <option key={x} value={x}>{t(`mailbox.sec.${x}`)}</option>)}</select></Field>
          </div>
        </details>

        <label className="field-checkbox"><input type="checkbox" checked={f.saveSent} onChange={(e) => set('saveSent', e.target.checked)} /><span>{t('mailbox.saveSent')}</span></label>
        <label className="field-checkbox"><input type="checkbox" checked={f.importReplies} onChange={(e) => set('importReplies', e.target.checked)} /><span>{t('mailbox.importReplies')}</span></label>
        <p className="field-hint">{t('mailbox.readOnlyHint')}</p>

        {test && (
          <ul className="mb-test" aria-live="polite">
            {['smtp', 'imap'].map((side) => (
              <li key={side} className={test[side].ok ? 'is-ok' : 'is-bad'}>
                <strong>{test[side].ok ? '✔' : '✖'} {side.toUpperCase()}</strong> — {test[side].ok ? t(`mailbox.test.${side}Ok`) : hint(test[side].error, side).replace(/^(SMTP|IMAP) — /, '')}
                {!test[side].ok && test[side].detail && <><br /><code className="small">{t('mailbox.serverSaid')} {test[side].detail}</code></>}
              </li>
            ))}
          </ul>
        )}
        <Alert kind={msg.kind || 'error'}>{msg.text}</Alert>
        <div className="ws-row">
          <button type="submit" className="btn btn-primary" disabled={!!busy}>{busy === 'test' ? t('mailbox.testing') : t('mailbox.saveTest')}</button>
          <button type="button" className="btn btn-ghost" disabled={!!busy} onClick={() => save(false)}>{busy === 'save' ? t('common.saving') : t('common.save')}</button>
          {m?.connected && m.importReplies && <button type="button" className="btn btn-ghost" disabled={!!busy} onClick={sync}>{busy === 'sync' ? t('common.loading') : t('mailbox.syncNow')}</button>}
          {m && <button type="button" className="btn btn-link is-danger" disabled={!!busy} onClick={disconnect}>{t('mailbox.remove')}</button>}
        </div>
      </form>

      {/* Email signature: its own editor and save (migration 039). */}
      <section className="card mb-form" id="signature">
        <h2>{t('mailbox.signature')}</h2>
        <SignatureEditor />
      </section>

      {data.manageRules && rulesDraft && (
        <form className="card mb-form mb-rules" onSubmit={saveRules}>
          <h2 className="acc-card-title">{t('mailbox.rules')}</h2>
          <p className="field-hint">{t('mailbox.rulesLead')}</p>
          <label className="field-checkbox"><input type="checkbox" checked={rulesDraft.requireOwn} onChange={(e) => { setRulesDraft((r) => ({ ...r, requireOwn: e.target.checked })); setRulesMsg({ kind: '', text: '' }); }} /><span>{t('mailbox.requireOwn')}</span></label>
          <label className="field-checkbox"><input type="checkbox" checked={rulesDraft.bccOwner} onChange={(e) => { setRulesDraft((r) => ({ ...r, bccOwner: e.target.checked })); setRulesMsg({ kind: '', text: '' }); }} /><span>{t('mailbox.bccOwner')}</span></label>
          <Alert kind={rulesMsg.kind || 'error'}>{rulesMsg.text}</Alert>
          <div className="ws-row"><button type="submit" className="btn btn-primary" disabled={busy === 'rules'}>{busy === 'rules' ? t('common.saving') : t('mailbox.rulesSave')}</button></div>
        </form>
      )}

      {data.manageRules && (
        <section className="card mb-form">
          <h2 className="acc-card-title">{t('mailbox.teamTitle')}</h2>
          {!team ? <p className="muted small">{t('common.loading')}</p> : team.length ? (
            <ul className="ws-list">
              {team.map((x) => (
                <li key={x.userId}>
                  <span className="ws-grow"><strong>{x.name}</strong><br /><span className="muted small">{x.email}</span></span>
                  <span className={`ws-badge${x.status === 'ok' ? ' is-ok' : ''}`}>{x.status === 'ok' ? '✔ ' : ''}{t(`mailbox.teamStatus.${x.status}`)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="muted small">{t('mailbox.teamNone')}</p>}
        </section>
      )}
    </div>
  );
}
